import React, { useState } from "react";
import {
  FaUserShield,
  FaUnlockAlt,
  FaExclamationTriangle,
} from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import useFormValidation from "../../hooks/useFormValidation";
import { validatePasswordField } from "../../components/shared/validationUtils";
import Notification from "../../components/shared/Notification";
import { changePassword } from "../../api/auth";

const AdminProfile = () => {
  const [activeSection, setActiveSection] = useState("general");
  const { user } = useAuth();
  const [notification, setNotification] = useState({
    show: false,
    type: "",
    message: "",
  });

  const adminInfo = user;

  const initialValues = {
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  };

  const submitPasswordChange = async (values) => {
    try {
      const passwordData = {
        current_password: values.currentPassword,
        new_password: values.newPassword,
      };

      const response = await changePassword(passwordData);

      setNotification({
        show: true,
        type: "success",
        message: "Contraseña actualizada correctamente",
      });

      setTimeout(() => {
        setNotification({ show: false, type: "", message: "" });
      }, 5000);

      resetForm();

      return response;
    } catch (error) {
      console.error("Error al cambiar la contraseña:", error);

      let errorMessage = "Error al actualizar la contraseña";

      if (error && error.errors) {
        const errorKeys = Object.keys(error.errors);
        if (errorKeys.length > 0) {
          errorMessage = error.errors[errorKeys[0]][0];
        }
      }

      setNotification({
        show: true,
        type: "error",
        message: errorMessage,
      });

      setTimeout(() => {
        setNotification({ show: false, type: "", message: "" });
      }, 5000);

      throw error;
    }
  };

  const {
    values: passwordData,
    errors,
    touched,
    isSubmitting,
    handleChange,
    handleBlur,
    handleSubmit,
    resetForm,
    validateAll,
  } = useFormValidation(
    initialValues,
    validatePasswordField,
    submitPasswordChange
  );

  const onSubmitForm = (e) => {
    e.preventDefault();

    if (validateAll()) {
      handleSubmit(e);
    } else {
      setNotification({
        show: true,
        type: "error",
        message: "Por favor corrige los errores en el formulario",
      });

      setTimeout(() => {
        setNotification({ show: false, type: "", message: "" });
      }, 5000);
    }
  };

  const renderActiveSection = () => {
    switch (activeSection) {
      case "general":
        return (
          <div className="admin-profile__section">
            <h3>Información General</h3>
            <div className="admin-profile__info-grid">
              <div className="info-item">
                <span className="info-label">Nombre completo</span>
                <span className="info-value">
                  {adminInfo.first_name} {adminInfo.last_name}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Correo electrónico</span>
                <span className="info-value">{adminInfo.email}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Rol de usuario</span>
                <span className="info-value">Administrador</span>
              </div>
              <div className="info-item">
                <span className="info-label">Nombre de usuario</span>
                <span className="info-value">{adminInfo.username}</span>
              </div>
            </div>
          </div>
        );
      case "seguridad":
        return (
          <div className="admin-profile__section">
            <h3>Seguridad de la Cuenta</h3>
            <form className="admin-profile__form" onSubmit={onSubmitForm}>
              <div className="form-group">
                <label htmlFor="currentPassword">Contraseña actual</label>
                <input
                  type="password"
                  id="currentPassword"
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={
                    touched.currentPassword && errors.currentPassword
                      ? "input-error"
                      : ""
                  }
                />
                {touched.currentPassword && errors.currentPassword && (
                  <div className="error-message">{errors.currentPassword}</div>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="newPassword">Nueva contraseña</label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={
                    touched.newPassword && errors.newPassword
                      ? "input-error"
                      : ""
                  }
                />
                {touched.newPassword && errors.newPassword && (
                  <div className="error-message">{errors.newPassword}</div>
                )}
              </div>
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirmar contraseña</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={
                    touched.confirmPassword && errors.confirmPassword
                      ? "input-error"
                      : ""
                  }
                />
                {touched.confirmPassword && errors.confirmPassword && (
                  <div className="error-message">{errors.confirmPassword}</div>
                )}
              </div>
              <button
                type="submit"
                className="btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Actualizando..." : "Cambiar contraseña"}
              </button>
            </form>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="admin-profile">
      <div className="admin-profile__header">
        <div className="admin-profile__avatar">
          <FaUserShield />
        </div>
        <div className="admin-profile__user-info">
          <h1>
            {adminInfo.firstName} {adminInfo.lastName}
          </h1>
          <div className="admin-profile__meta">
            <span className="admin-badge">Administrador</span>
            <span className="admin-profile__email">{adminInfo.email}</span>
          </div>
        </div>
      </div>

      <Notification
        show={notification.show}
        type={notification.type}
        message={notification.message}
        onClose={() => setNotification({ ...notification, show: false })}
      />

      <div className="admin-profile__content">
        <div className="admin-profile__sidebar">
          <div
            className={`sidebar-item ${
              activeSection === "general" ? "active" : ""
            }`}
            onClick={() => setActiveSection("general")}
          >
            <FaUserShield />
            <span>Información</span>
          </div>
          <div
            className={`sidebar-item ${
              activeSection === "seguridad" ? "active" : ""
            }`}
            onClick={() => setActiveSection("seguridad")}
          >
            <FaUnlockAlt />
            <span>Seguridad</span>
            {activeSection === "seguridad" &&
              Object.values(errors).some((error) => error !== "") &&
              Object.values(touched).some((t) => t) && (
                <span className="tab-status tab-status--invalid">
                  <FaExclamationTriangle />
                </span>
              )}
          </div>
        </div>

        <div className="admin-profile__main">{renderActiveSection()}</div>
      </div>
    </div>
  );
};

export default AdminProfile;
